export class Clock {

    private static lockDate: Date = null;

    static lock(date: Date): void {
        Clock.lockDate = date;
    }

    static currentDate(): Date {
        return Clock.lockDate ?? new Date();
    }

    /**
     * get current unix timestamp
     */
    static timestamp(): number {
        const now = this.currentDate();
        return now.getTime() / 1000;
    }

    static ageInSeconds(isoDate: string): number {
        const then = new Date(isoDate);
        const now = this.currentDate();
        return (now.getTime() - then.getTime()) / 1000;
    }

    static isoString(date: Date): string {
        return date.toISOString();
    }

    static now(): string {
        const now = this.currentDate();
        return now.toISOString();
    }

    static nowIso(): string {
        return this.isoDate(this.currentDate());
    }

    static isoDate(d: Date): string {
        return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    }

    static parse(s: string): Date {
        return new Date(s);
    }

    static parseToIso(s: string): string {
        const dt = Clock.parse(s);
        if (dt.getFullYear() > 2000) {
            return Clock.isoDate(dt);
        }
        return null;
    }

    static advanceDays(d: Date, c: number = 1): Date {
        const n = d.getTime();
        const r = new Date();
        r.setTime(n + c * (24 * 60 * 60 * 1000));
        return r;
    }

    static advanceDaysIso(d: string, c: number = 1): string {
        return Clock.isoDate(Clock.advanceDays(Clock.parse(d), c));
    }

    static lastDayOfMonth(d: Date): Date {
        let n = 31;
        if (d.getMonth() === 1) {
            n = d.getFullYear() % 4 === 0 ? 29 : 28;
        } else if (d.getMonth() === 3 || d.getMonth() === 5 || d.getMonth() === 8 || d.getMonth() === 10) {
            n = 30;
        }
        return new Date(d.getFullYear(), d.getMonth(), n);
    }

    static timeToMinutes(time: string): number {
        const hours = parseInt(time.substring(0, 2), 10);
        const minutes = parseInt(time.substring(3, 5), 10);
        return hours * 60 + minutes;
    }

    static minutesToTime(minutes: number): string {
        const dayMinutes = minutes % (60 * 24);
        const hours = '00' + Math.floor(dayMinutes / 60);
        const min = '00' + (minutes % 60);
        return hours.substring(hours.length - 2, hours.length) + ':' + min.substring(min.length - 2, min.length);
    }

    static advanceTime(startTime: string, minutes: number): string {
        const t = Clock.timeToMinutes(startTime) + minutes;
        return Clock.minutesToTime(t);
    }

    static dayNumber(
        date: string | Date,
        opts?: { pad2?: boolean } // keep it simple; always timezone-free for date-only strings
    ): string {
        // If it's a date-only string (YYYY-MM-DD), treat it as a pure calendar date.
        if (typeof date === 'string') {
            const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
            if (m) {
                const day = parseInt(m[3], 10);
                return opts?.pad2 ? String(day).padStart(2, '0') : String(day);
            }

            // Otherwise, fall back to native parsing (may include time/offset/Z).
            const d = new Date(date);
            if (Number.isNaN(d.getTime())) {
                throw new Error('Clock.dayNumber: invalid date string');
            }
            // Use UTC to avoid local-time shifts for ISO strings with offsets.
            const day = d.getUTCDate();
            return opts?.pad2 ? String(day).padStart(2, '0') : String(day);
        }

        // Date object: respect what's given (assume it's already the desired moment).
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            throw new Error('Clock.dayNumber: invalid Date');
        }
        const day = date.getDate();
        return opts?.pad2 ? String(day).padStart(2, '0') : String(day);
    }

    static inDays(days: number): Date
    {
        const d = new Date();
        const s = d.getTime() + days * 24 * 60 * 60 * 1000;
        d.setTime(s);
        return d;
    }
}
